import {useEffect, useState} from "react";
import {ChartDataModel, ChartModel} from "../models/ChartDataModel";
import {DashboardPieChartModel} from "../models/DashboardPieChartModel";
import {useDashboardContext} from "../dashboardContextProvider";
import {DashboardColors} from "../models/DashboardColors";
import {DashboardUddannelsesplanModel} from "./DashboardUddannelsesplanModel";
import {getEnums} from "../../../core/helpers/enumToList";
import {GroupByKeyAndApplyName} from "../dashboardUtils";
import {Localizer} from "../../../infrastructure/localization/localizer";
import {
    DashboardUddannelsesplanStatusEnum,
    GetUddannelsesplanStatusString
} from "../../vejledning/uddannelsesplan/DashboardUddannelsesplanStatusEnum";
import {
    getMinMaxDage,
    GetVejledningTimeString,
    VejledningDatoKategoriEnum
} from "../dashboardVejledersamtaler/VejledningDatoKategoriEnum";
import {
    CategoryCounts,
    DashboardAmountEnum,
    translateDashboardAmountEnum
} from "../dashboardVejledersamtaler/DashboardAmountEnum";
import {RoutePaths} from "../../../infrastructure/routes";
import {removeWhiteSpace} from "../../../core/helpers/stringHelper";
import {nameof} from "ts-simple-nameof";
import {DashboardListForloebModel} from "../dashboardList/models/DashboardListForloebModel";
import {InitialFilterModel} from "../../../core/components/reactTable/ReactBaseTable";
import {useHistory} from "react-router-dom";
import {getMinMaxValue} from "../dashboardVejledersamtaler/useDashboardVejledersamtaler";

const mapToPieChartModel = (chartModels: DashboardPieChartModel<DashboardUddannelsesplanModel>[], pieName: string): ChartModel => {
    let elementsPieOne: ChartDataModel[] = [];
    chartModels.forEach((chartModel, index) => {
        elementsPieOne.push(new ChartDataModel(chartModel.key, chartModel.name, chartModel.models.length, DashboardColors.GetVejledningColor(chartModel.name), chartModel.handleOnClick))
    })
    return {
        name: pieName,
        elements: elementsPieOne
    };
};

const mapToBarChartModel = (chartModel: CategoryCounts[], barName: string): ChartModel => {
    let elements: ChartDataModel[] = [];
    chartModel.forEach((chartModel, index) => {
        elements.push(new ChartDataModel(chartModel.amountGroupCategory.toString(), chartModel.groupCategoryName, chartModel.count, DashboardColors.UddannelsesplanerBarChartColor, chartModel.onCellClick))
    });

    return {
        name: barName,
        elements: elements
    };
};
const useDashboardUddannelsesplaner = () => {
    const {state} = useDashboardContext();
    const history = useHistory();

    const [uddannelsesplanerStatusPieChart, setUddannelsesplanerStatusPieChart] = useState<ChartModel[]>([]);
    const [uddannelsesplanerTidSidenSidste, setUddannelsesplanerTidSidenSidste] = useState<ChartModel[]>([]);
    const [uddannelsesplanerTidSidenSidsteTotalAmount, setUddannelsesplanerTidSidenSidsteTotalAmount] = useState<number>(0);
    const [uddannelsesplanerFoersteTotalAmount, setUddannelsesplanerFoersteTotalAmount] = useState<number>(0);
    const [uddannelsesplanerBarChart, setUddannelsesplanerBarChart] = useState<ChartModel>();

    useEffect(() => {
        const HandleUddannelsesplanType = (uddannelsesplaner: DashboardUddannelsesplanModel[]): ChartModel[] => {
            let pieModels: ChartModel[] = []
            const allStatusEnum = getEnums(DashboardUddannelsesplanStatusEnum);

            const uddannelsesplanerGrouperByForloeb = GroupByKeyAndApplyName(uddannelsesplaner, x => x.forloebId, x => x.forloebId);

            const oldestUddannelsesplanForEachForloeb = Object.keys(uddannelsesplanerGrouperByForloeb)
                .map(key => {
                    const sortedUddannelsesplaner = uddannelsesplanerGrouperByForloeb[key].items
                        .sort((a, b) => {
                            // Prioritize approved plans
                            if (a.status === DashboardUddannelsesplanStatusEnum.Godkendt && b.status !== DashboardUddannelsesplanStatusEnum.Godkendt) return -1;
                            if (a.status !== DashboardUddannelsesplanStatusEnum.Godkendt && b.status === DashboardUddannelsesplanStatusEnum.Godkendt) return 1;

                            // Sort by oprettetDato if afholdtDato is undefined
                            if (a.afholdtDato === undefined && b.afholdtDato !== undefined) return -1;
                            if (a.afholdtDato !== undefined && b.afholdtDato === undefined) return 1;

                            // Sort by oprettetDato
                            return a.oprettetDato.getTime() - b.oprettetDato.getTime();
                        });
                    return sortedUddannelsesplaner[0];
                });

            setUddannelsesplanerFoersteTotalAmount(oldestUddannelsesplanForEachForloeb.length);

            const uddannelsesplanerGrouped = GroupByKeyAndApplyName(oldestUddannelsesplanForEachForloeb, x => x.status.toString(), x => GetUddannelsesplanStatusString(x.status))

            allStatusEnum.forEach(enumValue => {
                if (!uddannelsesplanerGrouped[enumValue]) {
                    uddannelsesplanerGrouped[enumValue] = {
                        key: enumValue.toString(),
                        name: GetUddannelsesplanStatusString(enumValue),
                        items: []
                    };
                }
            });

            const handleUddannelsesplanStatusClick = (uddannelsesplanStatus: string) => {
                history.push({
                    pathname: RoutePaths.Dashboard({activeTab: removeWhiteSpace(Localizer.dashboard_listView()) }).url,
                    state: [
                        {
                            initialFilterId: nameof<DashboardListForloebModel>(x => x.foersteUddannelsesplanStatus).toLowerCase(),
                            initialFilterValue: [uddannelsesplanStatus]
                        },
                    ] as InitialFilterModel[]
                });
            };

            const uddannelsesplanerPieModel = Object.keys(uddannelsesplanerGrouped)
                .map(key => ({
                    key: key,
                    name: uddannelsesplanerGrouped[key].name,
                    models: uddannelsesplanerGrouped[key].items,
                    handleOnClick: () => handleUddannelsesplanStatusClick(uddannelsesplanerGrouped[key].name)
                })) as DashboardPieChartModel<DashboardUddannelsesplanModel>[]

            const uddannelsesplanerPieChart = mapToPieChartModel(uddannelsesplanerPieModel, Localizer.global_uddannelsesplaner())

            pieModels.push(uddannelsesplanerPieChart);
            return pieModels;
        }

        setUddannelsesplanerStatusPieChart(HandleUddannelsesplanType(state.uddannelsesplaner));

        const HandleUddannelsesplanTid = (uddannelsesplaner: DashboardUddannelsesplanModel[]): ChartModel[] => {
            const today = new Date();

            const uddannelsesplanerGrouperByForloeb = GroupByKeyAndApplyName(uddannelsesplaner, x => x.forloebId, x => x.forloebId);

            //Sorter uddannelsesplaner på afholdtDato og tag den nyeste for hvert forloeb
            const newestUddannelsesplanForEachForloeb = Object.keys(uddannelsesplanerGrouperByForloeb)
                .map(key => uddannelsesplanerGrouperByForloeb[key].items
                    .sort((a, b) => {
                        if (a.afholdtDato === undefined || a.afholdtDato === null) return 1;
                        if (b.afholdtDato === undefined || b.afholdtDato === null) return -1;
                        return b.afholdtDato.getTime() - a.afholdtDato.getTime();
                    })[0]);

            setUddannelsesplanerTidSidenSidsteTotalAmount(newestUddannelsesplanForEachForloeb.length);

            for (let uddannelsesplan of newestUddannelsesplanForEachForloeb) {
                if (uddannelsesplan.afholdtDato === null || uddannelsesplan.afholdtDato === undefined || isNaN(new Date(uddannelsesplan.afholdtDato).getTime())) {
                    uddannelsesplan.datoKategori = VejledningDatoKategoriEnum.IkkeAfholdt;
                } else if (uddannelsesplan.afholdtDato >= today.addMonths(-1)) {
                    uddannelsesplan.datoKategori = VejledningDatoKategoriEnum.UnderEnMaaned;
                } else if (uddannelsesplan.afholdtDato >= today.addMonths(-3)) {
                    uddannelsesplan.datoKategori = VejledningDatoKategoriEnum.OverEnMaaned;
                } else if (uddannelsesplan.afholdtDato >= today.addMonths(-6)) {
                    uddannelsesplan.datoKategori = VejledningDatoKategoriEnum.OverTreMaaneder;
                } else {
                    uddannelsesplan.datoKategori = VejledningDatoKategoriEnum.OverSeksMaaneder;
                }
            }

            let pieModels: ChartModel[] = []

            const allDatoKategoriEnums = getEnums(VejledningDatoKategoriEnum);
            const uddannelsesplanerGrouped = GroupByKeyAndApplyName(newestUddannelsesplanForEachForloeb, x => x.datoKategori.toString(), x => GetVejledningTimeString(x.datoKategori));
            allDatoKategoriEnums.forEach(enumValue => {
                if (!uddannelsesplanerGrouped[enumValue]) {
                    uddannelsesplanerGrouped[enumValue] = {
                        key: enumValue.toString(),
                        name: GetVejledningTimeString(enumValue),
                        items: []
                    };
                }
            });

            const handleSidsteUddannelsesplanClick = (datoCategory: [number | undefined, number | undefined]) => {
                history.push({
                    pathname: RoutePaths.Dashboard({activeTab: removeWhiteSpace(Localizer.dashboard_listView()) }).url,
                    state: [
                        {
                            initialFilterId: nameof<DashboardListForloebModel>(x => x.dageSidenSidsteUddannelsesplan).toLowerCase(),
                            initialFilterValue: datoCategory
                        },
                    ] as InitialFilterModel[]
                });
            };

            const uddannelsesplanerPieModel = Object.keys(uddannelsesplanerGrouped)
                .map(key => ({
                    key: key,
                    name: uddannelsesplanerGrouped[key].name,
                    models: uddannelsesplanerGrouped[key].items,
                    handleOnClick: () => handleSidsteUddannelsesplanClick(getMinMaxDage(uddannelsesplanerGrouped[key].items[0].datoKategori))
                })) as DashboardPieChartModel<DashboardUddannelsesplanModel>[]

            const uddannelsesplanerPieChart = mapToPieChartModel(uddannelsesplanerPieModel, Localizer.global_uddannelsesplaner())
            pieModels.push(uddannelsesplanerPieChart);
            return pieModels;
        }
        setUddannelsesplanerTidSidenSidste(HandleUddannelsesplanTid(state.uddannelsesplaner));


        const uddannelsesplanerCounts: CategoryCounts[] = [];
        const groupedUddannelsesplaner = GroupByKeyAndApplyName(state.uddannelsesplaner, x => x.forloebId, x => x.forloebId);

        const categoryCounts = {
            [DashboardAmountEnum.Nul]: 0,
            [DashboardAmountEnum.EnTilTo]: 0,
            [DashboardAmountEnum.TreTilFire]: 0,
            [DashboardAmountEnum.FemTilSeks]: 0,
            [DashboardAmountEnum.SyvTilTi]: 0,
            [DashboardAmountEnum.OverTi]: 0
        } as { [key: string]: number };

        Object.keys(groupedUddannelsesplaner).forEach(forloeb => {
            const count = groupedUddannelsesplaner[forloeb].items.filter(x => x.status !== DashboardUddannelsesplanStatusEnum.EndnuIkkeOprettet).length;
            if (count === 0)
                categoryCounts[DashboardAmountEnum.Nul]++;
            else if (count <= 2)
                categoryCounts[DashboardAmountEnum.EnTilTo]++;
            else if (count <= 4)
                categoryCounts[DashboardAmountEnum.TreTilFire]++;
            else if (count <= 6)
                categoryCounts[DashboardAmountEnum.FemTilSeks]++;
            else if (count <= 10)
                categoryCounts[DashboardAmountEnum.SyvTilTi]++;
            else if (count > 10)
                categoryCounts[DashboardAmountEnum.OverTi]++;
        });

        const handleAntalUddannelsesplanerClick = (minMaxValue: [number | undefined, number | undefined]) => {
            history.push({
                pathname: RoutePaths.Dashboard({activeTab: removeWhiteSpace(Localizer.dashboard_listView()) }).url,
                state: [
                    {
                        initialFilterId: nameof<DashboardListForloebModel>(x => x.antalUddannelsesplaner).toLowerCase(),
                        initialFilterValue: minMaxValue
                    },
                ] as InitialFilterModel[]
            });
        }

        getEnums(DashboardAmountEnum).forEach(enumValue => {
            uddannelsesplanerCounts.push({
                amountGroupCategory: enumValue,
                groupCategoryName: translateDashboardAmountEnum(enumValue),
                count: categoryCounts[enumValue],
                onCellClick: () => handleAntalUddannelsesplanerClick(getMinMaxValue(enumValue))
            });
        });

        const uddannelsesplanerCountChart = mapToBarChartModel(uddannelsesplanerCounts, Localizer.dashboard_uddannelsesplanerAmount());
        setUddannelsesplanerBarChart(uddannelsesplanerCountChart);
    }, [history, state.uddannelsesplaner])

    return {
        uddannelsesplanerTotalAmount: state.uddannelsesplaner.length,
        uddannelsesplanerFoersteTotalAmount: uddannelsesplanerFoersteTotalAmount,
        uddannelsesplanerIsLoading: state.uddannelsesplanerIsLoading,
        uddannelsesplanerStatusPieChart: uddannelsesplanerStatusPieChart,
        uddannelsesplanerTidSidenSidsteTotalAmount: uddannelsesplanerTidSidenSidsteTotalAmount,
        uddannelsesplanerTidSidenSidste: uddannelsesplanerTidSidenSidste,
        uddannelsesplanerBarChart: uddannelsesplanerBarChart
    }
}
export default useDashboardUddannelsesplaner;
